import { ArrowUpRight, ImageOff } from "lucide-react";
import { Photo } from "./photo";
import { cn } from "@hey404/ui";
import { Doodle, type DoodleName } from "./design-system";

/**
 * A course in the admin's Cursos list, in the same vocabulary as the
 * platform's course cards: a cover (the real thumbnail, or an accent fill
 * that says it still has none), status stickers on it, a paper body with
 * category, title and subtitle, the facts an admin scans for (modules,
 * classes, access and price) and an "Editar curso" foot.
 */

export interface AdminCourseCardProps {
  index: number;
  title: string;
  subtitle: string;
  category: string;
  thumbnailUrl: string | null;
  status: "draft" | "published" | "archived";
  saleStatus: "on_sale" | "coming_soon";
  access: "free" | "paid" | "subscription";
  price: string | null;
  modules: number;
  lessons: number;
  onOpen: () => void;
}

const covers = [
  { tone: "bg-acid", doodle: "flower-pink" },
  { tone: "bg-bubble", doodle: "face-lime" },
  { tone: "bg-lemon", doodle: "cursor-lavender" },
  { tone: "bg-grape", doodle: "key-cream" },
] as const satisfies ReadonlyArray<{ tone: string; doodle: DoodleName }>;

const statusStickers = {
  published: { label: "Publicado", tone: "bg-acid" },
  draft: { label: "Borrador", tone: "bg-paper" },
  archived: { label: "Archivado", tone: "bg-ink text-paper" },
} as const;

const accessLabels = {
  free: "Gratis",
  paid: "Pago único",
  subscription: "Suscripción",
} as const;

const Sticker = ({
  tone,
  tilt,
  children,
}: {
  tone: string;
  tilt: string;
  children: string;
}) => (
  <span
    className={cn(
      "border-ink border-chip shadow-brutal px-2 py-1 font-mono text-xs font-bold uppercase leading-none",
      tone,
      tilt,
    )}
  >
    {children}
  </span>
);

export function AdminCourseCard({
  index,
  title,
  subtitle,
  category,
  thumbnailUrl,
  status,
  saleStatus,
  access,
  price,
  modules,
  lessons,
  onOpen,
}: AdminCourseCardProps) {
  const cover = covers[index % covers.length]!;
  const number = String(index + 1).padStart(2, "0");
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Editar ${title}`}
      className="border-ink bg-paper border-surface shadow-brutal hover:shadow-brutal-lg group flex cursor-pointer flex-col overflow-hidden text-left transition-[translate,box-shadow] duration-200 hover:-translate-y-1 focus-visible:-translate-y-1"
    >
      <div
        className={cn(
          "border-ink border-b-(length:--border-surface) relative aspect-[16/9] overflow-hidden",
          cover.tone,
          "platform-grid",
        )}
      >
        {/* The accent art sits underneath; a working photo covers it, a
            missing or broken one leaves it with a note saying why. */}
        <span
          aria-hidden="true"
          className="font-display absolute bottom-2 left-4 text-7xl font-extrabold leading-none tracking-[-0.05em]"
        >
          {number}
        </span>
        <Doodle
          name={cover.doodle}
          tilt={index % 2 ? 8 : -8}
          className="absolute right-5 top-1/2 w-16 -translate-y-1/2 transition-transform duration-300 group-hover:rotate-6"
        />
        <Photo
          src={thumbnailUrl}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          fallback={
            <span className="bg-paper/85 border-ink absolute bottom-3 right-3 flex items-center gap-1.5 border px-2 py-1 font-mono text-[11px] font-bold">
              <ImageOff size={12} aria-hidden="true" />
              {thumbnailUrl ? "La portada no carga" : "Sin portada"}
            </span>
          }
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          <Sticker tone={statusStickers[status].tone} tilt="rotate-[-3deg]">
            {statusStickers[status].label}
          </Sticker>
          {saleStatus === "coming_soon" && (
            <Sticker tone="bg-lemon" tilt="rotate-[2deg]">
              Próximamente
            </Sticker>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        {category && (
          <p className="text-ink-soft font-mono text-xs font-bold uppercase tracking-[0.06em]">
            {category}
          </p>
        )}
        <h3 className="font-display mt-1.5 text-2xl font-extrabold leading-tight">
          {title}
        </h3>
        {subtitle && (
          <p className="text-ink-soft mt-2 line-clamp-2 text-sm leading-relaxed">
            {subtitle}
          </p>
        )}
        <dl className="mt-auto flex flex-wrap gap-x-4 gap-y-1 pt-4 font-mono text-xs">
          <div className="flex gap-1">
            <dt className="sr-only">Contenido</dt>
            <dd>
              {modules} {modules === 1 ? "módulo" : "módulos"} · {lessons}{" "}
              {lessons === 1 ? "clase" : "clases"}
            </dd>
          </div>
          <div className="flex gap-1">
            <dt className="sr-only">Acceso</dt>
            <dd className="font-bold">
              {accessLabels[access]}
              {access === "paid" && price ? ` · ${price}` : ""}
            </dd>
          </div>
        </dl>
      </div>
      <span className="border-ink border-t-(length:--border-chip) font-display group-hover:bg-paper-dim flex items-center justify-between px-5 py-3.5 font-extrabold transition-colors">
        Editar curso
        <ArrowUpRight
          size={18}
          aria-hidden="true"
          className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </span>
    </button>
  );
}
